import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogTitle,
	Label,
	Textarea,
} from "@exopengithub/exokit";
import { Button } from "@exopengithub/exokit/v2";
import {
	showErrorNotification,
	showSuccessNotification,
} from "molecules/notifications/notificationEvents";
import { DialogActions } from "molecules/dialog/DialogActions";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { clsx } from "clsx";
import { IconStar, IconStarFilled } from "@tabler/icons-react";

import { useResizeableTextarea } from "src/features/planning/comment/useResizableTextarea";
import type { AiSurveyQuestion } from "../api/aiChatApi";
import {
	useDismissAiNotificationMutation,
	useSubmitAiSurveyResponseMutation,
} from "../api/aiChatApi";

export type SurveyDismissReason = "submitted" | "snoozed" | "permanent";

interface AiFeedbackSurveyDialogProps {
	open: boolean;
	onClose: (reason: SurveyDismissReason) => void;
	companyDomainId: string;
	surveyKey: string;
	surveyVersion: number;
	questions: AiSurveyQuestion[];
}

type SectionBlock =
	| { kind: "text"; id: string }
	| { kind: "scale"; id: string; min: number; max: number }
	| {
			kind: "overall";
			scoreId: string;
			commentId: string | null;
			min: number;
			max: number;
	  };

const TEXT_MAX_CHARS = 4000;

function buildSections(questions: AiSurveyQuestion[]): SectionBlock[] {
	const score = questions.find(
		(q) => q.id === "overall_value_score" && q.type === "scale",
	);
	const comment = questions.find(
		(q) => q.id === "overall_value_comment" && q.type === "text",
	);

	const rest = questions
		.filter(
			(q) => q.id !== "overall_value_score" && q.id !== "overall_value_comment",
		)
		.map<SectionBlock>((q) =>
			q.type === "text"
				? { kind: "text", id: q.id }
				: { kind: "scale", id: q.id, min: q.min, max: q.max },
		);

	if (score && score.type === "scale") {
		return [
			...rest,
			{
				kind: "overall",
				scoreId: score.id,
				commentId: comment ? comment.id : null,
				min: score.min,
				max: score.max,
			},
		];
	}
	if (comment) {
		return [...rest, { kind: "text", id: comment.id }];
	}
	return rest;
}

function labelFor(id: string, t: (key: string) => string): string {
	switch (id) {
		case "usefulness_in_daily_work":
			return t("How well does {{aiProductName}} work in your day-to-day?");
		case "new_insights":
			return t("Have you discovered new insights in your data?");
		case "insights_led_to_action":
			return t("Have those insights led to any action or decision?");
		case "ease_of_use":
			return t("How easy is {{aiProductName}} to use?");
		case "overall_value_score":
			return t("How valuable is {{aiProductName}} overall?");
		case "overall_value_comment":
			return t("Short comment on your rating");
		default:
			return t("Feedback");
	}
}

function hintFor(id: string, t: (key: string) => string): string | null {
	switch (id) {
		case "usefulness_in_daily_work":
			return t(
				"(Speed, answer quality, whether it answers what you actually wondered)",
			);
		case "new_insights":
			return t("(Relationships, anomalies, patterns you hadn't seen before)");
		case "insights_led_to_action":
			return t(
				"(Changed ways of working, priorities, identified a risk/opportunity)",
			);
		case "ease_of_use":
			return t(
				"(Understanding it, formulating questions, getting started without help)",
			);
		default:
			return null;
	}
}

export function AiFeedbackSurveyDialog({
	open,
	onClose,
	companyDomainId,
	surveyKey,
	surveyVersion,
	questions,
}: AiFeedbackSurveyDialogProps) {
	const { t } = useTranslation();
	const [answers, setAnswers] = useState<Record<string, string | number>>({});
	const [submitSurvey, { isLoading: isSubmitting }] =
		useSubmitAiSurveyResponseMutation();
	const [dismissNotification, { isLoading: isDismissing }] =
		useDismissAiNotificationMutation();
	const explicitActionFired = useRef(false);

	const sections = useMemo(() => buildSections(questions), [questions]);
	const busy = isSubmitting || isDismissing;

	const setText = (id: string, value: string) => {
		setAnswers((prev) => {
			if (!value) {
				const { [id]: _omit, ...rest } = prev;
				return rest;
			}
			return { ...prev, [id]: value.slice(0, TEXT_MAX_CHARS) };
		});
	};

	const setScore = (id: string, value: number) => {
		setAnswers((prev) => ({ ...prev, [id]: value }));
	};

	const handleSubmit = async () => {
		explicitActionFired.current = true;
		try {
			await submitSurvey({
				companyDomainId,
				surveyKey,
				surveyVersion,
				answers,
			}).unwrap();
			showSuccessNotification({ message: t("Thank you for your feedback") });
			onClose("submitted");
		} catch {
			explicitActionFired.current = false;
			showErrorNotification({ message: t("Could not submit feedback") });
		}
	};

	const handleDismiss = async (mode: "snooze" | "permanent") => {
		explicitActionFired.current = true;
		try {
			await dismissNotification({
				companyDomainId,
				surveyKey,
				surveyVersion,
				mode,
			}).unwrap();
			onClose(mode === "snooze" ? "snoozed" : "permanent");
		} catch {
			explicitActionFired.current = false;
			showErrorNotification({ message: t("Could not dismiss notification") });
		}
	};

	const handleOpenChange = (isOpen: boolean) => {
		if (isOpen) return;
		if (explicitActionFired.current) {
			explicitActionFired.current = false;
			return;
		}
		void handleDismiss("snooze");
	};

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent size="lg">
				<DialogTitle>{t("We want to know what you think")}</DialogTitle>
				<DialogBody>
					<ol className="flex flex-col gap-6 py-2 list-none">
						{sections.map((section, index) => (
							<li key={sectionKeyFor(section)}>
								{renderSection(
									section,
									index + 1,
									answers,
									setText,
									setScore,
									t,
								)}
							</li>
						))}
					</ol>
				</DialogBody>
				<DialogActions>
					<Button
						type="button"
						variant="ghost"
						onClick={() => void handleDismiss("permanent")}
						isLoading={isDismissing}
						disabled={busy}
					>
						{t("Don't show again")}
					</Button>
					<Button
						type="button"
						variant="secondaryGray"
						onClick={() => void handleDismiss("snooze")}
						disabled={busy}
					>
						{t("Remind me later")}
					</Button>
					<Button
						type="button"
						onClick={() => void handleSubmit()}
						isLoading={isSubmitting}
						disabled={busy}
					>
						{t("Submit feedback")}
					</Button>
				</DialogActions>
			</DialogContent>
		</Dialog>
	);
}

function sectionKeyFor(section: SectionBlock): string {
	if (section.kind === "overall") return section.scoreId;
	return section.id;
}

function renderSection(
	section: SectionBlock,
	index: number,
	answers: Record<string, string | number>,
	onText: (id: string, value: string) => void,
	onScore: (id: string, value: number) => void,
	t: (key: string) => string,
) {
	if (section.kind === "text") {
		const value =
			typeof answers[section.id] === "string"
				? (answers[section.id] as string)
				: "";
		return (
			<TextQuestion
				index={index}
				title={labelFor(section.id, t)}
				placeholder={hintFor(section.id, t) ?? ""}
				value={value}
				onChange={(v) => onText(section.id, v)}
			/>
		);
	}

	if (section.kind === "scale") {
		const selected =
			typeof answers[section.id] === "number"
				? (answers[section.id] as number)
				: null;
		return (
			<ScaleQuestion
				index={index}
				title={labelFor(section.id, t)}
				min={section.min}
				max={section.max}
				value={selected}
				onChange={(v) => onScore(section.id, v)}
			/>
		);
	}

	const score =
		typeof answers[section.scoreId] === "number"
			? (answers[section.scoreId] as number)
			: null;
	const comment =
		section.commentId && typeof answers[section.commentId] === "string"
			? (answers[section.commentId] as string)
			: "";
	return (
		<OverallQuestion
			index={index}
			title={labelFor("overall_value_score", t)}
			min={section.min}
			max={section.max}
			scoreValue={score}
			onScoreChange={(v) => onScore(section.scoreId, v)}
			commentId={section.commentId}
			commentValue={comment}
			onCommentChange={(v) => {
				if (section.commentId) onText(section.commentId, v);
			}}
		/>
	);
}

function TextQuestion({
	index,
	title,
	placeholder,
	value,
	onChange,
}: {
	index: number;
	title: string;
	placeholder: string;
	value: string;
	onChange: (value: string) => void;
}) {
	const fieldId = useId();
	return (
		<>
			<Label htmlFor={fieldId}>
				{index}. {title}
			</Label>
			<AutoGrowTextarea
				id={fieldId}
				value={value}
				placeholder={placeholder}
				onChange={(e) => onChange(e.target.value)}
			/>
		</>
	);
}

function ScaleQuestion({
	index,
	title,
	min,
	max,
	value,
	onChange,
}: {
	index: number;
	title: string;
	min: number;
	max: number;
	value: number | null;
	onChange: (v: number) => void;
}) {
	const headingId = useId();
	return (
		<>
			<p
				id={headingId}
				className="font-medium text-sm text-gray-700 inline-block pb-[6px]"
			>
				{index}. {title}
			</p>
			<StarRating
				aria-labelledby={headingId}
				min={min}
				max={max}
				value={value}
				onChange={onChange}
			/>
		</>
	);
}

function OverallQuestion({
	index,
	title,
	min,
	max,
	scoreValue,
	onScoreChange,
	commentId,
	commentValue,
	onCommentChange,
}: {
	index: number;
	title: string;
	min: number;
	max: number;
	scoreValue: number | null;
	onScoreChange: (v: number) => void;
	commentId: string | null;
	commentValue: string;
	onCommentChange: (v: string) => void;
}) {
	const headingId = useId();
	const commentFieldId = useId();
	const { t } = useTranslation();
	return (
		<>
			<p
				id={headingId}
				className="font-medium text-sm text-gray-700 inline-block pb-[6px]"
			>
				{index}. {title}
			</p>
			<StarRating
				aria-labelledby={headingId}
				min={min}
				max={max}
				value={scoreValue}
				onChange={onScoreChange}
			/>
			{commentId && (
				<div className="mt-3">
					<Label htmlFor={commentFieldId}>
						{t("Short comment on your rating")}
					</Label>
					<AutoGrowTextarea
						id={commentFieldId}
						value={commentValue}
						placeholder=""
						onChange={(e) => onCommentChange(e.target.value)}
					/>
				</div>
			)}
		</>
	);
}

function AutoGrowTextarea({
	id,
	value,
	placeholder,
	onChange,
}: {
	id: string;
	value: string;
	placeholder: string;
	onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
}) {
	const ref = useRef<HTMLTextAreaElement>(null);
	useResizeableTextarea({ ref, value, enabled: true });
	return (
		<Textarea
			ref={ref}
			id={id}
			value={value}
			placeholder={placeholder}
			onChange={onChange}
			rows={2}
			resizable={false}
			className="min-h-[56px] max-h-[200px] overflow-y-auto"
		/>
	);
}

type StarAnim = {
	id: number;
	kind: "hover" | "wave";
	stars: number[];
	staggerMs: number;
};

function StarRating({
	min,
	max,
	value,
	onChange,
	"aria-labelledby": ariaLabelledby,
}: {
	min: number;
	max: number;
	value: number | null;
	onChange: (value: number) => void;
	"aria-labelledby"?: string;
}) {
	const [hovered, setHovered] = useState<number | null>(null);
	const [anim, setAnim] = useState<StarAnim | null>(null);
	const [glint, setGlint] = useState<{ index: number; id: number } | null>(
		null,
	);
	const animIdRef = useRef(0);
	const poppedRef = useRef<Set<number>>(new Set());

	const options: number[] = [];
	for (let i = min; i <= max; i++) options.push(i);
	const activeValue = hovered ?? value;

	useEffect(() => {
		if (hovered === null) return;
		const baseline = value ?? 0;
		const newStars: number[] = [];
		for (let i = baseline + 1; i <= hovered; i++) {
			if (!poppedRef.current.has(i)) {
				newStars.push(i);
				poppedRef.current.add(i);
			}
		}
		if (newStars.length > 0) {
			animIdRef.current += 1;
			setAnim({
				id: animIdRef.current,
				kind: "hover",
				stars: newStars,
				staggerMs: 30,
			});
		}
	}, [hovered, value]);

	useEffect(() => {
		if (!anim) return;
		const id = anim.id;
		const perStar = anim.kind === "hover" ? 250 : 300;
		const duration = perStar + anim.stars.length * anim.staggerMs + 50;
		const t = setTimeout(() => {
			setAnim((curr) => (curr?.id === id ? null : curr));
		}, duration);
		return () => clearTimeout(t);
	}, [anim]);

	useEffect(() => {
		if (!glint) return;
		const id = glint.id;
		const t = setTimeout(() => {
			setGlint((curr) => (curr?.id === id ? null : curr));
		}, 450);
		return () => clearTimeout(t);
	}, [glint]);

	const handleClick = (n: number) => {
		onChange(n);
		const wave: number[] = [];
		for (let i = min; i <= n; i++) wave.push(i);
		animIdRef.current += 1;
		const id = animIdRef.current;
		setAnim({ id, kind: "wave", stars: wave, staggerMs: 50 });
		setGlint({ index: n, id });
	};

	const handleLeave = () => {
		setHovered(null);
		poppedRef.current.clear();
	};

	return (
		<div
			className="flex flex-wrap gap-1"
			role="group"
			aria-labelledby={ariaLabelledby}
			onMouseLeave={handleLeave}
		>
			{options.map((n) => {
				const isFilled = activeValue !== null && n <= activeValue;
				const Icon = isFilled ? IconStarFilled : IconStar;
				const animIndex = anim?.stars.indexOf(n) ?? -1;
				const isAnimating = animIndex !== -1;
				const isGlinting = glint?.index === n;
				const animClass =
					isAnimating && anim
						? anim.kind === "hover"
							? "motion-safe:animate-star-pop"
							: "motion-safe:animate-star-wave"
						: "";
				const animKey = isAnimating ? `${anim!.id}-${n}` : `idle-${n}`;
				return (
					<button
						key={n}
						type="button"
						aria-pressed={value === n}
						aria-label={String(n)}
						onClick={() => handleClick(n)}
						onMouseEnter={() => setHovered(n)}
						className={clsx(
							"p-1 rounded-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-200",
							"transition-colors",
							isFilled ? "text-yellow-500" : "text-gray-300",
						)}
					>
						<span
							key={animKey}
							className={clsx("relative inline-flex", animClass)}
							style={
								isAnimating
									? { animationDelay: `${animIndex * anim!.staggerMs}ms` }
									: undefined
							}
						>
							<span
								key={isGlinting ? `glint-${glint!.id}` : "glint-idle"}
								className={clsx(
									"pointer-events-none absolute inset-0 rounded-full bg-yellow-400 opacity-0",
									isGlinting && "motion-safe:animate-star-glint",
								)}
								aria-hidden
							/>
							<Icon size={24} className="relative" />
						</span>
					</button>
				);
			})}
		</div>
	);
}
